/* eslint-disable react/prop-types */
import { useEffect } from "react";
import NumButton from "./NumButton";
import RemoveButton from "./RemoveButton";

const KEY_CONFIGS = [
  {
    value: "1",
    colors:
      "bg-sky-500/15 border border-sky-500/35 text-sky-300 hover:bg-sky-500/25 hover:border-sky-400 hover:text-sky-200 hover:shadow-[0_0_15px_rgba(56,189,248,0.25)]",
  },
  {
    value: "2",
    colors:
      "bg-emerald-500/15 border border-emerald-500/35 text-emerald-300 hover:bg-emerald-500/25 hover:border-emerald-400 hover:text-emerald-200 hover:shadow-[0_0_15px_rgba(52,211,153,0.25)]",
  },
  {
    value: "3",
    colors:
      "bg-purple-500/15 border border-purple-500/35 text-purple-300 hover:bg-purple-500/25 hover:border-purple-400 hover:text-purple-200 hover:shadow-[0_0_15px_rgba(168,85,247,0.25)]",
  },
  {
    value: "4",
    colors:
      "bg-amber-500/15 border border-amber-500/35 text-amber-300 hover:bg-amber-500/25 hover:border-amber-400 hover:text-amber-200 hover:shadow-[0_0_15px_rgba(245,158,11,0.25)]",
  },
  {
    value: "5",
    colors:
      "bg-teal-500/15 border border-teal-500/35 text-teal-300 hover:bg-teal-500/25 hover:border-teal-400 hover:text-teal-200 hover:shadow-[0_0_15px_rgba(20,184,166,0.25)]",
  },
  {
    value: "6",
    colors:
      "bg-orange-500/15 border border-orange-500/35 text-orange-300 hover:bg-orange-500/25 hover:border-orange-400 hover:text-orange-200 hover:shadow-[0_0_15px_rgba(249,115,22,0.25)]",
  },
  {
    value: "7",
    colors:
      "bg-pink-500/15 border border-pink-500/35 text-pink-300 hover:bg-pink-500/25 hover:border-pink-400 hover:text-pink-200 hover:shadow-[0_0_15px_rgba(236,72,153,0.25)]",
  },
  {
    value: "8",
    colors:
      "bg-indigo-500/15 border border-indigo-500/35 text-indigo-300 hover:bg-indigo-500/25 hover:border-indigo-400 hover:text-indigo-200 hover:shadow-[0_0_15px_rgba(99,102,241,0.25)]",
  },
  {
    value: "9",
    colors:
      "bg-lime-500/15 border border-lime-500/35 text-lime-300 hover:bg-lime-500/25 hover:border-lime-400 hover:text-lime-200 hover:shadow-[0_0_15px_rgba(132,204,22,0.25)]",
  },
];

const Keypad = ({ children, getValue, value = "", onEnter }) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      const { key, ctrlKey, altKey, metaKey } = event;

      if (ctrlKey || altKey || metaKey) {
        return;
      }

      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.isContentEditable)
      ) {
        return;
      }

      if (/^[0-9]$/.test(key)) {
        event.preventDefault();
        getValue((pre) => pre + key);
        return;
      }

      if (key === "Backspace" || key === "Delete") {
        event.preventDefault();
        getValue((pre) => pre.slice(0, -1));
        return;
      }

      if (key === "Escape" || key === "c" || key === "C") {
        event.preventDefault();
        getValue("");
        return;
      }

      if (key === "Enter" && typeof onEnter === "function") {
        event.preventDefault();
        onEnter();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [getValue, onEnter]);

  return (
    <div
      className="w-[220px] sm:w-[250px] flex flex-col items-center justify-center outline-none select-none"
      tabIndex={0}
    >
      {/* PIN Indicator Dots */}
      <div className="w-full flex justify-center pb-3">
        <div className="flex items-center justify-center gap-3.5 py-2.5 px-5 bg-color1/70 rounded-full border border-white/5 shadow-inner">
          {[0, 1, 2, 3, 4].map((index) => {
            const isFilled = value.length > index;
            return (
              <span
                key={index}
                className={`block w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full transition-all duration-200 ${
                  isFilled
                    ? "bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.9)] scale-110 border-2 border-amber-300"
                    : "bg-slate-700/60 border border-slate-600/40 scale-95"
                }`}
              />
            );
          })}
        </div>
      </div>

      {/* Symmetrical 3x4 Grid */}
      <div className="grid grid-cols-3 gap-2.5 sm:gap-3 pt-3 justify-items-center w-full">
        {KEY_CONFIGS.map((btn) => (
          <NumButton
            key={btn.value}
            onClick={() => getValue((pre) => pre + btn.value)}
            value={btn.value}
            colorClass={btn.colors}
          />
        ))}

        {/* 4th Row: C (Clear), 0, Backspace */}
        <button
          type="button"
          onClick={() => getValue("")}
          aria-label="Clear PIN"
          className="
            w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-2xl
            flex items-center justify-center 
            text-xl font-bold font-mono select-none cursor-pointer
            backdrop-blur-sm transition-all duration-150
            active:scale-95 shadow-sm
            bg-rose-500/20 border border-rose-500/40 text-rose-300
            hover:bg-rose-500/30 hover:border-rose-400/70 hover:text-rose-200
            hover:shadow-[0_0_15px_rgba(244,63,94,0.25)]
          "
        >
          <span>C</span>
        </button>

        <NumButton
          onClick={() => getValue((pre) => pre + "0")}
          value="0"
          colorClass="bg-cyan-500/15 border border-cyan-500/35 text-cyan-300 hover:bg-cyan-500/25 hover:border-cyan-400 hover:text-cyan-200 hover:shadow-[0_0_15px_rgba(6,182,212,0.25)]"
        />

        <RemoveButton onClick={() => getValue((pre) => pre.slice(0, -1))} />
      </div>

      {children && <div className="w-full pt-3.5 sm:pt-4">{children}</div>}
    </div>
  );
};

export default Keypad;
